import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useBrand } from '@/hooks/useBrand';

interface Props {
  title: string;
  subtitle?: string;
  data: string;
  /** Affiche le bouton de partage natif (si disponible). */
  shareable?: boolean;
  /** Affiche un bouton pour lancer le scan d'un autre QR. */
  onScan?: () => void;
  onClose: () => void;
}

/**
 * AccountQrModal — QR unique de compte avec le logo Zifek au centre.
 * Affichage, copie du lien et partage natif.
 */
export default function AccountQrModal({ title, subtitle, data, shareable = true, onScan, onClose }: Props) {
  const { brand } = useBrand();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const share = async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title, text: title, url: data });
      } catch {
        // partage annulé
      }
    } else {
      copy();
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed z-50 inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4">
        <div className="w-full sm:max-w-sm bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70">
          <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-qr-code-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-foreground-950 truncate">{title}</h3>
                {subtitle && <p className="text-xs text-foreground-500 truncate">{subtitle}</p>}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-foreground-500"></i>
            </button>
          </div>

          <div className="p-6 flex flex-col items-center gap-4">
            <div className="p-4 bg-white rounded-2xl border border-background-200/70">
              <QRCodeSVG
                value={data}
                size={200}
                level="H"
                fgColor="#172C43"
                bgColor="#ffffff"
                imageSettings={
                  brand.logo
                    ? {
                        src: brand.logo,
                        height: 44,
                        width: 44,
                        excavate: true,
                      }
                    : undefined
                }
              />
            </div>
            <p className="text-xs text-foreground-500 text-center leading-relaxed">
              Faites scanner ce code Zifek pour identifier le compte et lancer une opération.
            </p>
            <div className="w-full flex items-center gap-2 bg-background-100 rounded-lg px-3 py-2">
              <i className="ri-link text-foreground-400 text-sm flex-shrink-0"></i>
              <span className="text-xs text-foreground-600 truncate flex-1">{data}</span>
            </div>
            <div className="w-full flex items-center gap-2">
              <button
                onClick={copy}
                className="flex-1 h-11 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer flex items-center justify-center gap-2 bg-background-100 border border-background-200/70 text-foreground-700 hover:bg-background-200/70"
              >
                <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                {copied ? 'Copié !' : 'Copier'}
              </button>
              {shareable && (
                <button
                  onClick={share}
                  className="flex-1 h-11 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer flex items-center justify-center gap-2 bg-primary-500 text-background-50 hover:bg-primary-600"
                >
                  <i className="ri-share-forward-line"></i>
                  Partager
                </button>
              )}
            </div>
            {onScan && (
              <button
                onClick={onScan}
                className="w-full h-11 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer flex items-center justify-center gap-2 bg-background-100 border border-background-200/70 text-foreground-700 hover:bg-background-200/70"
              >
                <i className="ri-scan-line"></i>
                Scanner un QR code
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}